import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import styled, { keyframes } from 'styled-components';
import { navLinks, socialMedia, email } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

// Hidden on purpose — only opens with ⌘K / Ctrl+K. Nothing on the site advertises it.

const GROUPS = ['Navigate', 'Projects', 'Actions'];

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const StyledBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 15vh 20px 20px;
  background-color: rgba(10, 11, 12, 0.7);
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.15s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StyledPalette = styled.div`
  width: 100%;
  max-width: 580px;
  overflow: hidden;
  background-color: var(--navy);
  border: 1px solid var(--lightest-navy);
  border-radius: 8px;
  box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.8);
  animation: ${popIn} 0.2s var(--easing);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  .search {
    display: flex;
    align-items: center;
    padding: 0 20px;
    border-bottom: 1px solid var(--lightest-navy);

    .prompt {
      color: var(--green);
      font-family: var(--font-mono);
      font-size: var(--fz-lg);
      margin-right: 12px;
    }

    input {
      flex: 1;
      padding: 18px 0;
      border: 0;
      outline: 0;
      background: transparent;
      color: var(--lightest-slate);
      font-family: var(--font-sans);
      font-size: var(--fz-xl);

      &::placeholder {
        color: var(--dark-slate);
      }
    }
  }

  .results {
    max-height: min(400px, 55vh);
    overflow-y: auto;
    padding: 6px 0 10px;
    margin: 0;
    list-style: none;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: var(--lightest-navy) transparent;
  }

  .group {
    padding: 12px 20px 6px;
    color: var(--dark-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .item {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 0 8px;
    padding: 10px 12px;
    border-radius: var(--border-radius);
    color: var(--light-slate);
    font-size: var(--fz-lg);
    cursor: pointer;

    &.active {
      background-color: var(--green-tint);
      color: var(--lightest-slate);

      .lead {
        color: var(--green);
      }
    }

    .lead {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 20px;
      color: var(--slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      svg {
        display: block;
        width: 16px;
        height: 16px;
      }
    }

    // Calibre's glyphs sit high in their line box, so the label is nudged down to sit
    // on the row's visual centre alongside the lead and hint
    .label {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      transform: translateY(3px);
    }

    .hint {
      margin-left: auto;
      padding-left: 12px;
      flex-shrink: 0;
      color: var(--slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
    }

    &.active .hint {
      color: var(--green);
    }
  }

  .empty {
    padding: 30px 20px;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    text-align: center;
  }

  footer {
    display: flex;
    gap: 18px;
    padding: 12px 20px;
    border-top: 1px solid var(--lightest-navy);
    color: var(--dark-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);

    kbd {
      font-family: inherit;
      color: var(--slate);
    }

    .found {
      margin-left: auto;
      color: var(--green);
    }
  }
`;

const scoreItem = (item, terms) => {
  if (!terms.length) {
    return 1;
  }
  const label = item.label.toLowerCase();
  const haystack = `${label} ${item.keywords}`.toLowerCase();
  if (!terms.every(term => haystack.includes(term))) {
    return -1;
  }
  if (label.startsWith(terms[0])) {
    return 3;
  }
  return terms.every(term => label.includes(term)) ? 2 : 1;
};

const openExternal = url => window.open(url, '_blank', 'noopener,noreferrer');

const CommandPalette = ({ location }) => {
  const data = useStaticQuery(graphql`
    query {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
          }
        }
      }
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
          }
        }
      }
    }
  `);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const lastFocused = useRef(null);
  const pendingAction = useRef(null);
  const lastSurprise = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const close = () => setIsOpen(false);

  const goToSection = url => {
    const id = url.split('#')[1];
    const el = id && document.getElementById(id);
    if (location.pathname === '/' && el) {
      el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      window.history.replaceState(null, '', `#${id}`);
    } else {
      navigate(url);
    }
  };

  const items = useMemo(() => {
    const nav = [
      ...navLinks.map(({ name, url }, i) => ({
        group: 'Navigate',
        label: name,
        lead: `0${i + 1}.`,
        keywords: 'section',
        afterClose: true,
        run: () => goToSection(url),
      })),
      {
        group: 'Navigate',
        label: 'Archive',
        lead: '~',
        keywords: 'all projects table',
        afterClose: true,
        run: () => navigate('/archive'),
      },
    ];

    const seen = new Set();
    const projectLinks = [...data.projects.edges, ...data.featured.edges]
      .map(({ node }) => node.frontmatter)
      .filter(({ title, github, external }) => {
        if (!title || (!github && !external) || seen.has(title)) {
          return false;
        }
        seen.add(title);
        return true;
      })
      .map(({ title, tech, github, external }) => ({
        title,
        tech: tech || [],
        url: external || github,
        isLive: Boolean(external),
      }));

    const projects = projectLinks.map(({ title, tech, url, isLive }) => ({
      group: 'Projects',
      label: title,
      lead: <Icon name="Folder" />,
      hint: isLive ? 'live ↗' : 'code ↗',
      keywords: tech.join(' '),
      run: () => openExternal(url),
    }));

    const surpriseMe = () => {
      // Never the same project twice in a row
      const others = projectLinks.filter(({ title }) => title !== lastSurprise.current);
      const pool = others.length ? others : projectLinks;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      lastSurprise.current = pick.title;
      openExternal(pick.url);
    };

    const actions = [
      {
        group: 'Actions',
        label: 'Surprise me',
        lead: '✦',
        hint: 'random ↗',
        keywords: 'random lucky dice shuffle project',
        run: surpriseMe,
      },
      {
        group: 'Actions',
        label: 'Copy email',
        lead: '@',
        hint: 'copy',
        keywords: `${email} contact mail`,
        keepOpen: true,
        run: () => {
          navigator.clipboard.writeText(email).then(() => {
            setCopied(true);
            setTimeout(close, 900);
          });
        },
      },
      {
        group: 'Actions',
        label: 'Send an email',
        lead: '✉',
        keywords: `${email} contact mail hello`,
        run: () => {
          window.location.href = `mailto:${email}`;
        },
      },
      {
        group: 'Actions',
        label: 'View resume',
        lead: <Icon name="Bookmark" />,
        hint: 'pdf',
        keywords: 'cv resume',
        run: () => openExternal('/resume.pdf'),
      },
      ...socialMedia.map(({ name, url }) => ({
        group: 'Actions',
        label: `Open ${name}`,
        lead: <Icon name={name} />,
        hint: '↗',
        keywords: 'social profile',
        run: () => openExternal(url),
      })),
    ];

    return [...nav, ...projects, ...actions];
  }, [data, location.pathname, prefersReducedMotion]);

  const results = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return items
      .map((item, i) => ({ item, i, score: scoreItem(item, terms) }))
      .filter(({ score }) => score > 0)
      .sort(
        (a, b) =>
          GROUPS.indexOf(a.item.group) - GROUPS.indexOf(b.item.group) ||
          b.score - a.score ||
          a.i - b.i,
      )
      .map(({ item }) => item);
  }, [items, query]);

  // Global shortcut
  useEffect(() => {
    const onKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen(open => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Open / close side effects: reset state, lock scroll, manage focus
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    lastFocused.current = document.activeElement;
    setQuery('');
    setActiveIndex(0);
    setCopied(false);
    document.body.style.overflow = 'hidden';
    inputRef.current && inputRef.current.focus();

    return () => {
      document.body.style.overflow = '';
      lastFocused.current &&
        lastFocused.current.focus &&
        lastFocused.current.focus({ preventScroll: true });

      // Run the chosen action once scroll is unlocked, so smooth scrolling isn't cancelled
      if (pendingAction.current) {
        pendingAction.current();
        pendingAction.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    const el = isOpen && document.getElementById(`cmdk-item-${activeIndex}`);
    el && el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const runItem = item => {
    if (!item) {
      return;
    }
    if (item.keepOpen) {
      item.run();
      return;
    }
    // In-page navigation waits for the palette to close (scroll lock would cancel it); links
    // opening a new tab run straight away, since Safari only allows window.open during the event
    if (item.afterClose) {
      pendingAction.current = item.run;
      close();
      return;
    }
    item.run();
    close();
  };

  const onInputKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => (results.length ? (i + 1) % results.length : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => (results.length ? (i - 1 + results.length) % results.length : 0));
        break;
      case 'Enter':
        e.preventDefault();
        runItem(results[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        // Keep focus inside the palette
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <StyledBackdrop onMouseDown={e => e.target === e.currentTarget && close()}>
      <StyledPalette role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="search">
          <span className="prompt" aria-hidden="true">
            &gt;
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Where to?"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-results"
            aria-activedescendant={results.length ? `cmdk-item-${activeIndex}` : undefined}
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        {results.length ? (
          <ul className="results" id="cmdk-results" role="listbox">
            {results.map((item, i) => {
              const showGroup = i === 0 || results[i - 1].group !== item.group;
              const isCopy = item.keepOpen && copied;
              return (
                <React.Fragment key={`${item.group}-${item.label}`}>
                  {showGroup && (
                    <li className="group" role="presentation">
                      {item.group}
                    </li>
                  )}
                  {/* Keyboard selection is handled by the combobox input via aria-activedescendant */}
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                  <li
                    id={`cmdk-item-${i}`}
                    className={`item${i === activeIndex ? ' active' : ''}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    onMouseMove={() => i !== activeIndex && setActiveIndex(i)}
                    onClick={() => runItem(item)}>
                    <span className="lead" aria-hidden="true">
                      {item.lead}
                    </span>
                    <span className="label">{item.label}</span>
                    {(item.hint || isCopy) && (
                      <span className="hint">{isCopy ? 'copied ✓' : item.hint}</span>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        ) : (
          <div className="empty">Nothing matches “{query}”</div>
        )}

        <footer>
          <span>
            <kbd>↑↓</kbd> move
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
          <span className="found" aria-hidden="true">
            nice find.
          </span>
        </footer>
      </StyledPalette>
    </StyledBackdrop>
  );
};

CommandPalette.propTypes = {
  location: PropTypes.object.isRequired,
};

export default CommandPalette;
